/**
 * What the end-of-game screen says in its teal block (specs/first-game-summary.md §4).
 *
 * Phaser-free and string-free: it decides WHICH lines, `SummaryScene` turns them into text. That
 * split is what lets the decision table be unit-tested without a canvas or a translation file.
 */
import { milestoneReached } from '@/game/milestones';
import type { SessionSummary } from '@/game/progression/ReadingProgression';

export type SummaryLine =
  | { kind: 'stars'; count: number }
  | { kind: 'newlyMastered'; count: number; words: string[]; done: boolean }
  | { kind: 'upkeep'; mastered: number; total: number }
  /** The planet the in-game stack reached; `planet` is a `scoreboard.planets.*` key. */
  | { kind: 'firstFlight'; planet: string }
  | { kind: 'almost'; count: number }
  | { kind: 'keepGoing' };

/** The first game of a lesson — the only one the anti-cramming gate can leave with 0 stars. */
export function isFirstGame(summary: Pick<SessionSummary, 'sessionCount'>): boolean {
  return summary.sessionCount === 1;
}

/**
 * Gains win and are shown exactly as before. Only the empty case changes: instead of "keep going"
 * over a bar that did not move, a first game says where it flew, and any game says how many words
 * are one step from a star. At most two lines when nothing was earned.
 */
export function summaryLines(summary: SessionSummary, stackStars: number): SummaryLine[] {
  const done = summary.stats.abcPlanetReached;
  const lines: SummaryLine[] = [];

  if (summary.starsEarned > 0) lines.push({ kind: 'stars', count: summary.starsEarned });
  if (summary.newlyMastered.length > 0) {
    lines.push({
      kind: 'newlyMastered', count: summary.newlyMastered.length, words: summary.newlyMastered, done,
    });
  }
  if (lines.length > 0) return lines;

  // Journey complete: there is nothing left to be "almost".
  if (done) return [{ kind: 'upkeep', mastered: summary.stats.mastered, total: summary.stats.total }];

  if (isFirstGame(summary)) {
    const planet = milestoneReached(stackStars);
    if (planet) lines.push({ kind: 'firstFlight', planet });
  }
  if (summary.almostWords > 0) lines.push({ kind: 'almost', count: summary.almostWords });
  return lines.length > 0 ? lines : [{ kind: 'keepGoing' }];
}
